import React, { useEffect, useState, useMemo } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { AppTheme, Colors } from 'styles/theme';
import Flex from 'components/common/Flex';
import { ArrowLeftHomeSvg, ArrowRightHomeSvg } from 'assets/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCreative, Pagination, Navigation } from 'swiper';
import { useTranslation } from 'react-i18next';
import useDepsContainer from 'hooks/useDepsContainer';
import 'swiper/css';
import 'swiper/css/effect-creative';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/swiper-bundle.css';
import './InviteSwiper.css';

const IntroSwiper = () => {
  const styles = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const { uiStore } = useDepsContainer();
  const { t, i18n } = useTranslation();
  const cachedT = useMemo(() => t, [t, uiStore.language]);
  const [swiperLeft, setSwiperLeft] = useState(0);
  const [swiperIndex, setSwiperIndex] = useState(0);

  const gotoPre = () => {
    if (swiperIndex === 0) {
      return;
    } else if (swiperIndex === 1) {
      setSwiperIndex(0);
    } else if (swiperIndex === 2) {
      setSwiperIndex(1);
      setSwiperLeft(0);
    }
  };

  const gotoNext = () => {
    if (swiperIndex === 0) {
      setSwiperIndex(1);
    } else if (swiperIndex === 1) {
      setSwiperIndex(2);
      setSwiperLeft(410);
    } else if (swiperIndex === 2) {
      return;
    }
  };

  const swiperHover = index => {
    setSwiperIndex(index);
  };

  return (
    <Flex className={mdDown || lgDown ? styles.contentMd : styles.content} column={mdDown || lgDown}>
      <Flex className={mdDown ? styles.privacyWrapMd : lgDown ? styles.privacyWrapLg : styles.privacyWrap} column>
        <Flex className={mdDown ? styles.textMd : lgDown ? styles.textLg : styles.text}>
          {cachedT('pumpkinPrivacyGuarantee')}
        </Flex>
        <Flex className={mdDown ? styles.titleMd : styles.title}>{cachedT('privacyFirstPriority')}</Flex>
        <Flex className={styles.titleBg}></Flex>
      </Flex>

      {!mdDown && !lgDown && (
        <Flex className={styles.swiperWrap}>
          <Flex
            className={styles.swiperContent}
            style={{
              marginLeft: `-${swiperLeft}px`
            }}
          >
            <Flex
              className={swiperIndex === 0 ? styles.selected1 : styles.unselect1}
              column
              onMouseEnter={() => {
                swiperHover(0);
              }}
            >
              <Flex className={styles.swiperText}>{cachedT('censorshipResistance')}</Flex>
              <Flex className={styles.swiperDesc}>{cachedT('endToEndEncryption')}</Flex>
            </Flex>
            <Flex
              className={swiperIndex === 1 ? styles.selected2 : styles.unselect2}
              column
              onMouseEnter={() => {
                swiperHover(1);
              }}
            >
              <Flex className={styles.swiperText}>{cachedT('yourKeyYourAccount')}</Flex>
              <Flex className={styles.swiperDesc}>{cachedT('securePumpkinAccount')}</Flex>
            </Flex>
            <Flex
              className={swiperIndex === 2 ? styles.selected3 : styles.unselect3}
              column
              onMouseEnter={() => {
                swiperHover(2);
              }}
            >
              <Flex className={styles.swiperText}>{cachedT('Anonymity')}</Flex>
              <Flex className={styles.swiperDesc}>{cachedT('selectivePictureSharing')}</Flex>
            </Flex>
          </Flex>
        </Flex>
      )}
      {!mdDown && !lgDown && (
        <Flex className={styles.control} column>
          <Flex
            className={swiperIndex === 0 ? styles.btnPreDisabled : styles.btnPre}
            onClick={() => {
              gotoPre();
            }}
          ></Flex>
          <Flex
            className={swiperIndex === 2 ? styles.btnNextDisabled : styles.btnNext}
            onClick={() => {
              gotoNext();
            }}
          ></Flex>
        </Flex>
      )}
      {(mdDown || lgDown) && (
        <Swiper
          pagination={true}
          modules={[Pagination]}
          className={styles.swiperList}
          style={{ padding: mdDown ? '0' : '24px' }}
          slidesPerView={mdDown ? 1 : 2}
        >
          <SwiperSlide className={mdDown ? styles.slide : styles.slideLg}>
            <Flex className={styles.listTitle}>{cachedT('censorshipResistance')}</Flex>
            <Flex className={styles.listDesc}>{cachedT('endToEndEncryption')}</Flex>
            <Flex className={styles.listImg1}></Flex>
          </SwiperSlide>
          <SwiperSlide className={mdDown ? styles.slide : styles.slideLg}>
            <Flex className={styles.listTitle}>{cachedT('Anonymity')}</Flex>
            <Flex className={styles.listDesc}>{cachedT('securePumpkinAccount')}</Flex>
            <Flex className={styles.listImg2}></Flex>
          </SwiperSlide>
          <SwiperSlide className={mdDown ? styles.slide : styles.slideLg}>
            <Flex className={styles.listTitle}>{cachedT('yourKeyYourAccount')}</Flex>
            <Flex className={styles.listDesc}>{cachedT('selectivePictureSharing')}</Flex>
            <Flex className={styles.listImg3}></Flex>
          </SwiperSlide>
        </Swiper>
      )}
    </Flex>
  );
};

const useStyles = makeStyles((theme: AppTheme) =>
  createStyles({
    content: {
      width: '100%',
      maxWidth: '1328px',
      padding: '120px 24px 0',
      margin: '0 auto',
      background: '#FFF',
      height: '780px',
      position: 'relative'
    },
    contentMd: {
      margin: '0 auto',
      background: '#FFF',
      width: '100%',
      height: 'auto',
      paddingTop: '0'
    },
    privacyWrap: {
      width: '430px',
      height: '640px',
      paddingTop: '204px'
    },
    privacyWrapMd: {
      width: 'calc(100% - 32px)',
      height: 'auto',
      paddingTop: '0',
      margin: '0 auto'
    },
    privacyWrapLg: {
      width: 'calc(100% - 100px)',
      height: 'auto',
      paddingTop: '0',
      margin: '0 auto'
    },
    text: {
      color: '#18181B',
      fontSize: '24px',
      fontWeight: '300',
      lineHeight: '32px',
      letterSpacing: '-0.3px',
      marginBottom: '11px',
      width: '400px'
    },
    textLg: {
      color: '#18181B',
      fontSize: '24px',
      fontWeight: '300',
      lineHeight: '32px',
      letterSpacing: '-0.3px',
      marginBottom: '11px',
      width: '100%'
    },
    textMd: {
      color: '#18181B',
      fontSize: '16px',
      fontWeight: '300',
      lineHeight: '20px',
      letterSpacing: '-0.3px',
      marginBottom: '0'
    },
    title: {
      // width: '442px',
      color: '#18181B',
      fontFamily: 'Lexend',
      fontSize: '64px',
      fontWeight: '900',
      lineHeight: '78px'
      // background: 'url(/images/swiper-privacy-title.svg) center no-repeat',
      // backgroundSize: 'cover'
    },
    titleMd: {
      color: '#18181B',
      fontFamily: 'Lexend',
      fontSize: '40px',
      fontWeight: '900',
      lineHeight: '48px'
    },
    titleBg: {
      width: '189px',
      height: '56px',
      background: 'url(images/title-icon3.svg) left no-repeat',
      backgroundSize: '189px 56px'
    },
    swiperWrap: {
      width: '800px',
      height: '640px',
      overflow: 'hidden'
    },
    swiperContent: {
      width: '1200px',
      height: '640px',
      transition: 'margin-left 0.3s ease-in-out',
      cursor: 'pointer'
    },
    selected1: {
      width: '400px',
      height: '640px',
      background: 'url(/images/swiper-privacy-selected1.png) center no-repeat',
      backgroundSize: '100%',
      alignItems: 'center',
      paddingTop: '40px',
      transition: 'all 0.3s ease'
    },
    selected2: {
      width: '400px',
      height: '640px',
      background: 'url(/images/swiper-privacy-selected2.png) center no-repeat',
      backgroundSize: '100%',
      alignItems: 'center',
      paddingTop: '40px',
      transition: 'all 0.3s ease'
    },
    selected3: {
      width: '400px',
      height: '640px',
      background: 'url(/images/swiper-privacy-selected3.png) center no-repeat',
      backgroundSize: '100%',
      alignItems: 'center',
      paddingTop: '40px',
      transition: 'all 0.3s ease'
    },
    unselect1: {
      width: '400px',
      height: '640px',
      background: 'url(/images/swiper-privacy-unselect1.png) center bottom  no-repeat',
      backgroundSize: '229px 233px',
      alignItems: 'center',
      paddingTop: '156px'
    },
    unselect2: {
      width: '400px',
      height: '640px',
      background: 'url(/images/swiper-privacy-unselect2.png) center bottom  no-repeat',
      backgroundSize: '229px 312px',
      alignItems: 'center',
      paddingTop: '156px'
    },
    unselect3: {
      width: '400px',
      height: '640px',
      background: 'url(/images/swiper-privacy-unselect3.png) center bottom  no-repeat',
      backgroundSize: '229px 312px',
      alignItems: 'center',
      paddingTop: '156px'
    },
    swiperText: {
      width: 'calc(100% - 32px)',
      color: '#18181B',
      textAlign: 'center',
      fontSize: '24px',
      fontWeight: '700',
      lineHeight: '52px',
      letterSpacing: '-0.5px',
      height: '52px',
      alignItems: 'center',
      justifyContent: 'center'
    },
    swiperDesc: {
      width: 'calc(100% - 32px)',
      color: '#000',
      textAlign: 'center',
      fontSize: '16px',
      fontWeight: '300',
      lineHeight: '24px'
    },
    card: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end'
    },
    control: {
      width: '40px',
      height: '640px',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '10px'
      // position: 'absolute',
      // right: '-50px',
      // top: '140px'
    },
    btnPre: {
      width: '40px',
      height: '40px',
      background: 'url(/images/home-swiper-next.svg) center no-repeat',
      backgroundSize: '40px 40px',
      transform: 'rotate(180deg)',
      alignItems: 'center',
      margin: '7px 0',
      cursor: 'pointer'
    },
    btnPreDisabled: {
      width: '40px',
      height: '40px',
      background: 'url(/images/home-swiper-pre-disable.svg) center no-repeat',
      backgroundSize: '40px 40px',
      alignItems: 'center',
      margin: '7px 0'
    },
    btnNext: {
      width: '40px',
      height: '40px',
      background: 'url(/images/home-swiper-next.svg) center no-repeat',
      backgroundSize: '40px 40px',
      margin: '7px 0',
      cursor: 'pointer'
    },
    btnNextDisabled: {
      width: '40px',
      height: '40px',
      background: 'url(/images/home-swiper-pre-disable.svg) center no-repeat',
      backgroundSize: '40px 40px',
      transform: 'rotate(180deg)',
      alignItems: 'center',
      margin: '7px 0'
    },
    swiperList: {
      width: '100%',
      padding: '0 0',
      margin: '24px 0',
      overflow: 'visible'
    },
    slide: {
      background: '#FFF !important',
      width: 'calc(100% - 76px) !important',
      height: '494px',
      borderRadius: '24px 24px 0px 0px',
      borderTop: '14px solid #000',
      borderRight: '14px solid #000',
      borderLeft: '14px solid #000',
      margin: '0 24px !important',
      flexDirection: 'column',
      alignItems: 'flex-start !important',
      justifyContent: 'normal !important',
      position: 'relative'
    },
    slideLg: {
      background: '#FFF !important',
      width: 'calc(50% - 72px) !important',
      height: '494px',
      borderRadius: '24px 24px 0px 0px',
      borderTop: '14px solid #000',
      borderRight: '14px solid #000',
      borderLeft: '14px solid #000',
      margin: '0 24px !important',
      flexDirection: 'column',
      alignItems: 'flex-start !important',
      justifyContent: 'normal !important',
      position: 'relative'
    },
    listTitle: {
      width: 'calc(100% - 20px)',
      color: '#18181B',
      textAlign: 'center',
      fontSize: '24px',
      fontWeight: '500',
      lineHeight: '32px',
      letterSpacing: '-0.5px',
      margin: '15px auto 10px',
      alignItems: 'center',
      justifyContent: 'center'
    },
    listDesc: {
      width: 'calc(100% - 20px)',
      color: '#000',
      fontSize: '16px',
      fontWeight: '300',
      lineHeight: '24px',
      margin: '0 auto'
    },
    listImg1: {
      width: '250px',
      height: '292px',
      background: 'url(/images/mobile/privacy-image1.png) center no-repeat',
      backgroundSize: '250px 292px',
      position: 'absolute',
      bottom: '0',
      left: '50%',
      marginLeft: '-125px'
    },
    listImg2: {
      width: '250px',
      height: '292px',
      background: 'url(/images/mobile/privacy-image2.png) center no-repeat',
      backgroundSize: '250px 292px',
      position: 'absolute',
      bottom: '0',
      left: '50%',
      marginLeft: '-125px'
    },
    listImg3: {
      width: '250px',
      height: '292px',
      background: 'url(/images/mobile/privacy-image3.png) center no-repeat',
      backgroundSize: '250px 292px',
      position: 'absolute',
      bottom: '0',
      left: '50%',
      marginLeft: '-125px'
    }
  })
);

export default observer(IntroSwiper);
